import API_BASE from '../../config';
import axios from 'axios';
import { addResourceToGrid } from '../../Utils/worldHelpers';
import { convertTileType, updateGridResource } from '../../Utils/GridManagement';
import { validateTileType, enrichResourceFromMaster } from '../../Utils/ResourceHelpers';
import { refreshPlayerAfterInventoryUpdate, spendIngredients } from '../../Utils/InventoryManagement';
import FloatingTextManager from '../../UI/FloatingText';
import farmState from '../../FarmState';
import { trackQuestProgress } from '../Quests/QuestGoalTracker';
import { getCurrentTileCoordinates } from '../../Utils/ResourceHelpers';
import GlobalGridStateTilesAndResources from '../../GridState/GlobalGridStateTilesAndResources';
import { tryAdvanceFTUEByTrigger } from '../FTUE/FTUEutils';
import { createCollectEffect, createPlantGrowEffect } from '../../VFX/VFX';
import soundManager from '../../Sound/SoundManager';

/**
 * Determines the appropriate error string for an invalid planting attempt.
 * Checks all validon[x] fields to find which tile types ARE valid.
 * - If exactly 1 tile type is valid, returns the specific error string for that tile.
 * - If multiple or zero valid tile types, returns the default "Can't plant here" (304).
 *
 * Tile type to string mapping:
 * - d (dirt) → 303 "Must plant on dirt"
 * - z (moss) → 307 "Must plant on moss"
 * - n (sand) → 308 "Must plant on sand"
 * - fallback → 304 "Can't plant here"
 */
const getPlantingErrorString = (farmplotItem) => {
  // Map of tile type codes to their specific error strings
  const tileTypeToString = {
    'd': 303, // dirt
    'z': 307, // moss
    'n': 308, // sand
  };

  // All possible validon fields to check
  const validonFields = ['g', 'd', 's', 'p', 'w', 'l', 'n', 'o', 'x', 'y', 'z'];

  // Find all tile types that are valid for this farmplot
  const validTileTypes = validonFields.filter(tileCode => {
    const fieldName = `validon${tileCode}`;
    return farmplotItem[fieldName] === true;
  });

  // If exactly one valid tile type and we have a specific string for it
  if (validTileTypes.length === 1 && tileTypeToString[validTileTypes[0]]) {
    return tileTypeToString[validTileTypes[0]];
  }

  // Otherwise return default "Can't plant here"
  return 304;
};

export const handleFarmPlotPlacement = async ({
  selectedItem,
  TILE_SIZE,
  resources,
  setResources,
  currentPlayer,
  setCurrentPlayer,
  inventory,
  setInventory,
  backpack,
  setBackpack,
  gridId,
  masterResources,
  masterSkills,
  updateStatus,
  overridePosition, // Optional: { x, y } to plant at a specific tile instead of player position
}) => {
  try {
    console.log(masterResources);

    let tileX, tileY;
    if (overridePosition) {
      // Use the override position (from cursor mode click)
      tileX = overridePosition.x;
      tileY = overridePosition.y;
    } else {
      // Use player's current position
      const coords = getCurrentTileCoordinates(gridId, currentPlayer);
      if (!coords) return false;
      tileX = coords.tileX;
      tileY = coords.tileY;
    }

    const tileOccupied = resources.find((res) => res.x === tileX && res.y === tileY);
    if (tileOccupied) {
      FloatingTextManager.addFloatingText(304, tileX, tileY, TILE_SIZE); // "Occupied"
      return false;
    }

    // Use local tile data instead of async server call for instant response
    const tiles = GlobalGridStateTilesAndResources.getTiles();
    const tileType = tiles?.[tileY]?.[tileX];

    // Check if the tile type is valid for this farmplot using validon[x] fields
    const validonField = `validon${tileType}`;
    const isValidTile = selectedItem[validonField] === true;

    if (!tileType || !isValidTile) {
      // Determine appropriate error message based on valid tile types for this farmplot
      const errorStringId = getPlantingErrorString(selectedItem);
      FloatingTextManager.addFloatingText(errorStringId, tileX, tileY, TILE_SIZE);
      return false;
    }

    const didSpend = await spendIngredients({
      playerId: currentPlayer.playerId,
      recipe: selectedItem,
      inventory,
      backpack,
      setInventory,
      setBackpack,
      setCurrentPlayer,
      updateStatus,
    });
    if (!didSpend) {
      FloatingTextManager.addFloatingText(305, tileX, tileY, TILE_SIZE); // "Missing ingredients"
      return false;
    }

    // Timer setup
    const growEndTime = Date.now() + (selectedItem.growtime || 0) * 1000;
    console.log(`⏱️ Planting ${selectedItem.type}: growtime = ${selectedItem.growtime}, growEndTime = ${growEndTime}, secondsFromNow = ${(growEndTime - Date.now()) / 1000}`);

    // Lookup from masterResources for enrichment
    console.log("🌱 Attempting to plant:", selectedItem.type, "at", tileX, tileY);
    const enrichedNewResource = enrichResourceFromMaster(
      {
        type: selectedItem.type,
        x: tileX,
        y: tileY,
        growEnd: growEndTime,
      },
      masterResources
    );
    console.log("🌾 Enriched resource before setResources:", enrichedNewResource);

    // Start grow animation BEFORE updating state so PixiRenderer skips rendering this resource
    // This prevents the resource from appearing before the animation plays
    if (enrichedNewResource.symbol) {
      createPlantGrowEffect(tileX, tileY, TILE_SIZE, enrichedNewResource.symbol, null, enrichedNewResource.filename);
    }

    // Optimistically update local client state (after VFX marks resource as animating)
    const existing = GlobalGridStateTilesAndResources.getResources() || [];
    GlobalGridStateTilesAndResources.setResources([...existing, enrichedNewResource]);
    console.log("🧪 Global resources after planting:", GlobalGridStateTilesAndResources.getResources());
    setResources([...existing, enrichedNewResource]);

    // Update server and all clients
    const gridUpdateResponse = await updateGridResource(
      gridId,
      {
        type: selectedItem.type,
        x: tileX,
        y: tileY,
        growEnd: growEndTime,
      },
      true
    );

    if (gridUpdateResponse?.success) {
      farmState.addSeed({
        type: selectedItem.type,
        x: tileX,
        y: tileY,
        growEnd: growEndTime,
        output: selectedItem.output, // ✅ required for crop conversion
      });

      // Show collect VFX and floating text for planting
      // Note: createPlantGrowEffect is called earlier (before setResources) to ensure
      // PixiRenderer skips rendering the resource during animation
      createCollectEffect(tileX, tileY, TILE_SIZE);
      FloatingTextManager.addFloatingText(302, tileX, tileY, TILE_SIZE); // "Planted!"
      soundManager.playSFX('plant');

      // Track quest progress for "Plant" actions
      // Use the output (crop) name instead of the plot name for quest tracking
      const cropName = selectedItem.output || selectedItem.type;
      await trackQuestProgress(currentPlayer, 'Plant', cropName, 1, setCurrentPlayer);

      // Try to advance FTUE if this is the player's first crop planted
      await tryAdvanceFTUEByTrigger('PlantedFirstCrop', currentPlayer.playerId, currentPlayer, setCurrentPlayer);

      return true; // Success!
    } else {
      throw new Error('Server update failed.');
    }
  } catch (error) {
    console.error('❌ Error in handleFarmPlotPlacement:', error);
    return false;
  }
};



export const handleTerraform = async ({ TILE_SIZE, actionType, tileType, gridId, currentPlayer, tileTypes, setTileTypes, overridePosition, isDeveloper }) => {

  console.log("handleTerraform;  currentPlayer = ",currentPlayer);

  if (!currentPlayer?.location) {
    console.error("❌ handleTerraform: Missing currentPlayer location.");
    return false;
  }

  // Use override position if provided (cursor mode), otherwise use player position
  let tileX, tileY;
  if (overridePosition) {
    tileX = overridePosition.x;
    tileY = overridePosition.y;
  } else {
    const coords = getCurrentTileCoordinates(gridId, currentPlayer);
    if (!coords) return false;
    tileX = coords.tileX;
    tileY = coords.tileY;
  }

  const tile = tileTypes?.[tileY]?.[tileX];
  console.log("tile at ",tileY,", ",tileX," = ",tile);

  if (!tile) {
    console.error("❌ handleTerraform: Could not find tile at given coordinates.");
    return false;
  }
  if (tile === 'l' && !isDeveloper) {
      FloatingTextManager.addFloatingText(320, tileX, tileY, TILE_SIZE);
    return false;
  }
  if (tile === 'w' && !isDeveloper) {
      FloatingTextManager.addFloatingText(320, tileX, tileY, TILE_SIZE); // Same message as lava for now
    return false;
  }

  let newType;

  // If tileType is directly provided, use it (new dynamic approach)
  if (tileType) {
    newType = tileType;
  } else {
    // Legacy: Determine the new tile type based on the action (for backwards compatibility)
    switch (actionType) {
      case "till":
        newType = "d";
        break;
      case "plantGrass":
        newType = "g";
        break;
      case "pave":
        newType = "p";
        break;
      case "stone":
        newType = "s";
        break;
      case "cobblestone":
        newType = "x";
        break;
      case "water":
        newType = "w";
        break;
      default:
        console.error(`❌ handleTerraform: Unknown actionType "${actionType}"`);
        return false;
    }
  }

  console.log(`🌱 handleTerraform: Changing tile at (${tileX}, ${tileY}) to "${newType}"`);

  // Call convertTileType to update the tile in the database and emit the change
  await convertTileType(gridId, tileX, tileY, newType, setTileTypes);

  // Show VFX for terraforming
  createCollectEffect(tileX, tileY, TILE_SIZE);
  soundManager.playSFX('terraform');
  return true; // Success
};
